import React, {
  createContext,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import {useHistory} from 'react-router-dom';
import isEqual from 'lodash.isequal';
import sortBy from 'lodash.sortby';
import useUndoableState from '../hooks/useUndoableState';
import {
  FILTERABLE_PARAM_PREFIX,
  NO_FILTER_QUERY_PARAMS,
  NO_PREFIX_FILTERS,
} from '../constants';
import ensureValuesAreArrays from '../utils/ensureValuesAreArrays';
import sanitizeQueryParams from '../utils/sanitizeQueryParams';
import isFilterableParam from '../utils/isFilterableParam';

const qsOptions = {
  arrayFormat: 'separator',
  arrayFormatSeparator: ',',
};

const arraysAreEqual = (a1, a2) => isEqual(sortBy(a1), sortBy(a2));

export const FiltersContext = createContext({});

// Returns all querystring parameters to use for filtering
const getFiltersFromQueryParams = querystring => {
  const allQueryParams = qs.parse(querystring, qsOptions);
  return sanitizeQueryParams(allQueryParams);
};

/*
  @param defaults - An object who's keys are the filters name and the values are an array of strings.
  example:
  {
   size: ['sm', 'lg'],
   color: ['black']
  }
 */
export const FiltersContextProvider = ({children, defaults = {}}) => {
  const history = useHistory();

  const initialValues = useRef(
    ensureValuesAreArrays(getFiltersFromQueryParams(history.location.search)),
  );

  const [filters, setFilters, {reset, undo, canUndo, past}] = useUndoableState(
    initialValues.current,
  );

  // Converts filters into querystring
  const filtersToQueryParams = (theFilters, currentSearchParams = {}) => {
    const theCurrentSearchParams = {...currentSearchParams};
    const filterableParams = {};

    // Remove filterable params from the current
    // search params in the URL.
    Object.keys(theCurrentSearchParams).forEach(searchParam => {
      if (
        isFilterableParam(searchParam) ||
        NO_PREFIX_FILTERS.includes(searchParam)
      ) {
        delete theCurrentSearchParams[searchParam];
      }
    });

    Object.keys(theFilters).forEach(filter => {
      let filterName = filter;
      if (!NO_PREFIX_FILTERS.includes(filter)) {
        filterName = `${FILTERABLE_PARAM_PREFIX}${filter}`;
      }
      filterableParams[filterName] = filters[filter];
    });

    return qs.stringify(
      {...theCurrentSearchParams, ...filterableParams},
      qsOptions,
    );
  };

  const filtersWithDefaults = useMemo(() => {
    return {
      ...defaults,
      ...filters,
    };
  }, [defaults, filters]);

  const filtersWithoutDefaults = useMemo(
    () =>
      Object.keys(filters).reduce((acc, curr) => {
        const value = filters[curr];

        // is same as default
        if (defaults[curr] && arraysAreEqual(defaults[curr], value)) {
          return acc;
        }

        return {
          ...acc,
          [curr]: value,
        };
      }, {}),
    [defaults, filters],
  );

  // persist filters in query parameters
  useEffect(() => {
    const querystring = filtersToQueryParams(
      filtersWithoutDefaults,
      qs.parse(history.location.search),
    );

    history.replace({
      search: querystring,
    });
  }, [filters]); // eslint-disable-line

  const handleSetFilters = useCallback(
    newFilters => {
      // go to page 1 when changing filters
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('p');

      history.replace({
        search: searchParams.toString(),
      });

      setFilters(newFilters);
    },
    [history, setFilters],
  );

  const hasAppliedFilters = useMemo(() => {
    const filterValuesAsArray = Object.entries(filtersWithoutDefaults);

    for (let i = 0; i < filterValuesAsArray.length; i += 1) {
      const [currFilterKey, currFilterValues] = filterValuesAsArray[i];

      if (
        !NO_FILTER_QUERY_PARAMS.includes(currFilterKey) &&
        currFilterValues.length > 0
      )
        return true;
    }

    return false;
  }, [filtersWithoutDefaults]);

  return (
    <FiltersContext.Provider
      value={{
        filters: filtersWithDefaults,
        filtersWithoutDefaults,
        setFilters: handleSetFilters,
        hasAppliedFilters,
        filtersToQueryParams,
        reset,
        undo,
        canUndo,
        past,
      }}>
      {children}
    </FiltersContext.Provider>
  );
};

FiltersContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaults: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
};
